import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { api } from '../api.js'
import { useAuth } from '../auth/AuthContext.jsx'

// Admin → Announcements: the history, and the manual queue drain.
//
// There is no edit and no delete anywhere on this screen, by design. An
// announcement is a record of something already delivered to inboxes and
// phones; editing it afterwards would make this list lie about what people
// actually received. The compose screen's preview is what replaces "edit".
//
// "Send pending now" is the session-guarded twin of the announcements-queue
// cron — same batch, same retry rules. It exists because the cron runs every
// few minutes and an admin who just pressed send wants to watch it go out,
// and because a local install has no cron at all.

export function AdminAnnouncementsPage() {
  const { t, i18n } = useTranslation()
  const { user: me, loading: authLoading } = useAuth()

  const [items, setItems] = useState(null)
  const [settings, setSettings] = useState(null)
  const [detail, setDetail] = useState(null)      // the expanded announcement
  const [detailId, setDetailId] = useState(null)
  const [drained, setDrained] = useState(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  function reload() {
    setError(null)
    api.admin.announcements.list()
      .then((d) => { setItems(d.items || []); setSettings(d.settings || null) })
      .catch((e) => { setError(e.code || 'server'); setItems([]) })
  }

  useEffect(() => {
    if (authLoading || !me || me.role !== 'admin') return
    reload()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, me])

  async function fetchDetail(id) {
    setDetail(null)
    try { setDetail(await api.admin.announcements.get(id)) }
    catch (e) { setError(e.code || 'server') }
  }

  function toggleDetail(id) {
    if (detailId === id) { setDetailId(null); setDetail(null); return }
    setDetailId(id)
    fetchDetail(id)
  }

  async function drain() {
    setBusy(true); setError(null); setDrained(null)
    try {
      const d = await api.admin.announcements.queue()
      setDrained(d)
      reload()
      // The open row's counters have just moved — re-read it rather than
      // leaving a stale breakdown on screen.
      if (detailId) fetchDetail(detailId)
    } catch (e) { setError(e.code || 'server') }
    finally { setBusy(false) }
  }

  const fmtDate = (s) => {
    if (!s) return '—'
    const d = new Date(String(s).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return s
    return d.toLocaleDateString(i18n.language === 'en' ? 'en-GB' : 'ro-RO', {
      day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  }

  if (authLoading) return <main className="container"><p>{t('common.loading')}</p></main>
  if (!me || me.role !== 'admin') {
    return <main className="container"><p className="error">{t('admin.errors.not_admin')}</p></main>
  }

  // How many rows are still waiting anywhere — drives the drain button.
  const pending = (items || []).reduce(
    (n, a) => n + Math.max(0, (a.recipients || 0) - (a.sent || 0) - (a.failed || 0) - (a.skipped || 0)),
    0,
  )

  return (
    <main className="container">
      <p><Link to="/admin">&larr; {t('admin.title')}</Link></p>
      <h1>{t('admin_announcements.title')}</h1>
      <p className="muted">{t('admin_announcements.intro')}</p>

      {error && (
        <p className="error">
          {t(`admin_announcements.errors.${error}`, {
            defaultValue: t(`admin.errors.${error}`, { defaultValue: error }),
          })}
        </p>
      )}

      <div className="form-actions">
        <Link to="/admin/announcements/new">
          <button type="button">{t('admin_announcements.new')}</button>
        </Link>
        <button type="button" disabled={busy || pending === 0} onClick={drain}>
          {busy ? t('common.loading') : t('admin_announcements.drain')}
        </button>
      </div>

      <p className="muted small">
        {pending > 0
          ? t('admin_announcements.pending_hint', { count: pending })
          : t('admin_announcements.queue_empty')}
        {settings ? ' ' + t('admin_announcements.batch_hint', { batch: settings.queue_batch }) : null}
      </p>

      {drained && (
        <p className="success">
          {t('admin_announcements.drained', {
            processed: drained.processed ?? 0,
            sent: drained.sent ?? 0,
            failed: drained.failed ?? 0,
          })}
        </p>
      )}

      {items === null && <p>{t('common.loading')}</p>}

      {items !== null && items.length === 0 && (
        <p className="muted">{t('admin_announcements.none')}</p>
      )}

      {items !== null && items.length > 0 && (
        <table className="results-table">
          <thead>
            <tr>
              <th>{t('admin_announcements.col_sent_at')}</th>
              <th>{t('admin_announcements.col_channel')}</th>
              <th>{t('admin_announcements.col_audience')}</th>
              <th>{t('admin_announcements.col_subject')}</th>
              <th>{t('admin_announcements.col_recipients')}</th>
              <th>{t('admin_announcements.col_status')}</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <Row
                key={a.id} a={a} t={t} fmtDate={fmtDate}
                open={detailId === a.id} detail={detailId === a.id ? detail : null}
                onToggle={() => toggleDetail(a.id)}
              />
            ))}
          </tbody>
        </table>
      )}
    </main>
  )
}

function Row({ a, t, fmtDate, open, detail, onToggle }) {
  const filters = []
  if ((a.courses || []).length) filters.push((a.courses || []).map((c) => c.name).join(', '))
  if (Number(a.filter_unpaid) === 1)         filters.push(t('admin_announcements.filter_unpaid_short'))
  if (Number(a.filter_no_enrollments) === 1) filters.push(t('admin_announcements.filter_no_enrollments_short'))

  return (
    <>
      <tr>
        <td>{fmtDate(a.published_at)}</td>
        <td><span className="badge">{t(`admin_announcements.channel_${a.channel}`)}</span></td>
        <td>
          {t(`admin_announcements.audience_${a.audience}`)}
          {filters.length > 0 && (
            <div className="muted small">{filters.join(' · ')}</div>
          )}
        </td>
        <td>{a.subject || <span className="muted">{t('admin_announcements.no_subject')}</span>}</td>
        <td>
          {a.recipients ?? 0}
          {a.channel !== 'site' && (
            <div className="muted small">
              {t('admin_announcements.counts', {
                sent: a.sent ?? 0, failed: a.failed ?? 0, skipped: a.skipped ?? 0,
              })}
            </div>
          )}
        </td>
        <td>
          {/* announcements.status is queued | sending | done (sql/016) —
              `site` jumps straight to done, the queued channels walk it. */}
          <span className={`badge ${a.status === 'done' ? 'ok-badge' : a.status === 'sending' ? 'pending-badge' : 'muted-badge'}`}>
            {t(`admin_announcements.status_${a.status}`, { defaultValue: a.status })}
          </span>
        </td>
        <td>
          <button type="button" className="link-button" onClick={onToggle}>
            {open ? t('admin_announcements.hide') : t('admin_announcements.details')}
          </button>
        </td>
      </tr>

      {open && (
        <tr>
          <td colSpan={7}>
            {!detail && <p className="muted">{t('common.loading')}</p>}
            {detail && <Detail d={detail} t={t} />}
          </td>
        </tr>
      )}
    </>
  )
}

function Detail({ d, t }) {
  const b = d.breakdown || {}
  const a = d.announcement || {}
  const failures = d.failures || []

  return (
    <div className="announcement-detail">
      <p className="muted small">
        {t('admin_announcements.breakdown', {
          pending: b.pending ?? 0, sent: b.sent ?? 0,
          skipped: b.skipped ?? 0, failed: b.failed ?? 0,
        })}
      </p>

      {a.subject ? <h3>{a.subject}</h3> : null}
      <div className="announcement-body" dangerouslySetInnerHTML={{ __html: a.body || '' }} />

      {failures.length > 0 && (
        <>
          <h4>{t('admin_announcements.failures_title')}</h4>
          <p className="muted small">{t('admin_announcements.failures_hint')}</p>
          <table className="results-table">
            <thead>
              <tr>
                <th>{t('admin_announcements.col_who')}</th>
                <th>{t('admin_announcements.col_status')}</th>
                <th>{t('admin_announcements.col_attempts')}</th>
                <th>{t('admin_announcements.col_error')}</th>
              </tr>
            </thead>
            <tbody>
              {failures.map((f) => (
                <tr key={f.user_id}>
                  <td>{f.name}<div className="muted small">{f.email || f.phone || ''}</div></td>
                  <td>{t(`admin_announcements.rstatus_${f.status}`, { defaultValue: f.status })}</td>
                  <td>{f.attempts ?? 0}</td>
                  <td className="small">
                    {f.last_error === 'no_phone'
                      ? t('admin_announcements.err_no_phone')
                      : (f.last_error || '—')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}
